import { Link } from '@inertiajs/react'
import { LanguageFlag } from '@/components/ui/LanguageFlag'
import { useStaggerIn } from '@/hooks/useStaggerIn'
import { englishName } from '@/content/languages'
import { cn } from '@/utils/cn'
import type { Language } from '@/types/language'

interface EnrolledLanguagesListProps {
  /** Already filtered to the learner's enrolled languages. */
  languages: Language[]
  activeId: number | null
}

/**
 * Columns only once there are rows to fill them: a lone language stretched across the
 * desktop shell reads as an empty page, and two columns of one item each is worse.
 */
function columnsFor(count: number): string {
  if (count < 2) return ''
  if (count < 5) return 'sm:grid-cols-2'
  return 'sm:grid-cols-2 xl:grid-cols-3'
}

/**
 * Minimal dashboard list — hairline-separated rows (no card backgrounds) matching the
 * landing language rows. Each row links to its language panel and shows a quiet
 * "Continue →" hint — muted by default (still AA-contrast, visible on touch devices),
 * accent on hover/focus, and always emphasised for the active language.
 */
export function EnrolledLanguagesList({ languages, activeId }: EnrolledLanguagesListProps) {
  const listRef = useStaggerIn<HTMLUListElement>(languages.length > 0)
  return (
    <ul
      ref={listRef}
      className={cn('grid border-t border-line-soft gap-x-10', columnsFor(languages.length))}
    >
      {languages.map((lang) => {
        const active = lang.id === activeId
        return (
          <li key={lang.id} className="border-b border-line-soft">
            <Link
              href={`/languages/${lang.id}`}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'group flex items-center gap-4 min-h-[56px] py-4 transition-colors duration-200 rounded',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                active ? 'text-em' : 'text-body hover:text-accent',
              )}
            >
              <LanguageFlag isoCode={lang.iso_code} size="m" />
              <span className="flex flex-col">
                <span className={cn('font-display text-lg leading-snug', active && 'text-em')}>
                  {lang.name}
                </span>
                <span className="font-mono text-xs uppercase tracking-widest text-muted">
                  {englishName(lang.iso_code, lang.name)}
                </span>
              </span>
              <span
                className={cn(
                  'ml-auto font-mono text-xs uppercase tracking-widest transition-colors duration-200',
                  active
                    ? 'text-em'
                    : 'text-muted group-hover:text-accent group-focus-visible:text-accent',
                )}
              >
                Continue →
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
